import React from "react";
import { message, Switch, Tooltip } from "antd";
import { useUiLang } from "utils/uiLanguage";
import type { ManualOnlyState } from "api/fastApi/manualOnly";
import type { IAudioState } from "providers/AudioProvider/types";
import type { IAudioHandles } from "providers/AudioProvider/handleTypes";

/**
 * «Без автоправок — только ручная работа».
 *
 * Выключатель — в настройках (группа «Режим работы»), знак — в шапке редактора: режим меняет,
 * что сохраняется при каждой правке, и работать в нём, не видя этого, нельзя. Флаг один на
 * пару лекции и лежит на сервере — его видят все, кто её откроет, и сборка финального видео.
 */

type SwitchState = Pick<
    IAudioState,
    "manualOnly" | "manualOnlyState" | "manualOnlyApplicable" | "manualOnlyLoading" | "manualOnlyError"
> &
    Partial<Pick<IAudioState, "manualOnlyStream">>;

/**
 * Живой эфир: включённый режим останавливает дубляж этой пары. Сервер редактора отказывает эфиру
 * (409) в подготовке, озвучке и пересборке её папок, а сервер эфира сегодня считает такой отказ
 * сбоем папки и повторяет её до двух часов — и всё это время стоит вся очередь эфира.
 */
const streamOnConfirm = (en: boolean) =>
    en
        ? "This is a live stream. Switch automatic edits off for this pair?\n\n" +
          "Live dubbing of this pair will stop: the editor backend will refuse the stream's preparation, voicing and rebuilding of this pair's folders. " +
          "The stream server currently treats that refusal as a failed folder and retries it for up to 2 hours — meanwhile the other languages and the next folders of the stream wait too.\n\n" +
          "If the stream is over and no new folders are coming, nothing of this happens."
        : "Это живой эфир. Выключить автоправки для этой пары?\n\n" +
          "Живой дубляж этой пары остановится: сервер редактора будет отказывать эфиру в подготовке, озвучке и пересборке её папок. " +
          "Сервер эфира сейчас считает такой отказ сбоем папки и повторяет её до 2 часов — всё это время ждут и другие языки, и следующие папки эфира.\n\n" +
          "Если эфир закончен и новые папки не приходят — ничего этого не случится.";

const formatSetAt = (iso?: string) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export function ManualOnlySwitch({
    state,
    handles,
}: {
    state: SwitchState;
    handles: Pick<IAudioHandles, "setManualOnly">;
}) {
    const en = useUiLang() === "en";
    if (!state.manualOnlyApplicable) return null;

    const onChange = async (checked: boolean) => {
        // Включение у лекции только останавливает машину — спрашивать не о чем. У живого эфира
        // оно останавливает и его дубляж пары (см. streamOnConfirm): спрашиваем и передаём
        // серверу подтверждение. Выключение возвращает автоправки, которые снова начнут двигать
        // расставленное руками, — это стоит подтвердить.
        if (checked && state.manualOnlyStream) {
            if (!window.confirm(streamOnConfirm(en))) return;
            const result = await handles.setManualOnly(true, undefined, { streamAck: true });
            if (result?.error) {
                message.error(result.error);
            }
            return;
        }
        if (
            !checked &&
            !window.confirm(
                en
                    ? "Turn automatic edits back on for this pair?\n\nAfter a drag, a move or a re-voice the editor and the server will again lay neighbouring voicings out by themselves — moving ones that do not overlap too, pulling them earlier toward the source start — and may fit speed again, including placements made by hand."
                    : "Снова включить автоправки для этой пары?\n\nПосле перетаскивания, сдвига и переозвучки редактор и сервер снова будут раскладывать соседние озвучки сами — сдвигать и те, что не наехали, тянуть раньше, к началу оригинала, — и подгонять скорость, в том числе у расставленных руками.",
            )
        ) {
            return;
        }
        const result = await handles.setManualOnly(checked);
        if (result?.error) {
            message.error(result.error);
        }
    };

    const setBy = state.manualOnlyState?.set_by;
    const setAt = formatSetAt(state.manualOnlyState?.set_at);

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <Switch
                checked={state.manualOnly}
                loading={state.manualOnlyLoading}
                onChange={(checked) => void onChange(checked)}
            />
            {state.manualOnly && (setBy || setAt) ? (
                <span style={{ fontSize: 11, color: "#8c8c8c", textAlign: "right" }}>
                    {en ? "on" : "включил"} {setBy || "—"}
                    {setAt ? `, ${setAt}` : ""}
                </span>
            ) : null}
            {state.manualOnlyError ? (
                <span role="alert" style={{ fontSize: 11, color: "#cf1322", textAlign: "right" }}>
                    {state.manualOnlyError}
                </span>
            ) : null}
        </div>
    );
}

/** Знак режима в шапке: пока он горит, правка одной реплики сохраняет одну реплику. */
export function ManualOnlyBadge({
    manualOnly,
    state,
}: {
    manualOnly: boolean;
    state?: ManualOnlyState | null;
}) {
    const en = useUiLang() === "en";
    if (!manualOnly) return null;
    const who = state?.set_by ? (en ? ` Switched on by ${state.set_by}.` : ` Включил ${state.set_by}.`) : "";
    return (
        <Tooltip
            title={
                (en
                    ? "Manual work only for this language pair: the editor does not re-voice, re-translate or speed up anything for you, and the buttons that edit the folder by themselves (prepare and rebuild the voicing, resync, translate the folder and the like) are off. The one thing moved for you: a voicing that runs over the previous one is pushed later, to that end + 50 ms — never earlier; each push is named. The final video is built from the files as they are; a voicing that still overlaps is placed by the build right at the previous one's end. Switch: Settings → Working mode."
                    : "Только ручная работа для этой пары: редактор не переозвучивает, не переводит и не ускоряет за вас, а кнопки, которые правят папку сами (подготовка и пересоздание озвучки, ресинк, перевод папки и подобные), выключены. Сдвигается за вас одно: озвучка, наехавшая на предыдущую, уезжает позже — на её конец + 50 мс, раньше никогда; каждый такой сдвиг называется. Финальное видео собирается из файлов как есть; озвучку, которая всё ещё наезжает, сборка ставит ровно на конец предыдущей. Выключатель: Настройки → Режим работы.") +
                who
            }
        >
            <span
                data-testid="manual-only-badge"
                style={{
                    marginLeft: 10,
                    padding: "2px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    borderRadius: 4,
                    border: "1px solid #ffbb96",
                    backgroundColor: "#fff2e8",
                    color: "#d4380d",
                    whiteSpace: "nowrap",
                }}
            >
                {en ? "No automatic edits" : "Без автоправок"}
            </span>
        </Tooltip>
    );
}
